import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Fade,
  Slide,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Security as SecurityIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import ApiService from '../../services/api';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedUser, setSelectedUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('create'); // 'create', 'edit', 'view'
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'user',
    company: '',
    phone: '',
    isActive: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Keine Mock-Daten mehr - echte API-Integration

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, roleFilter, statusFilter]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await ApiService.getUsers();
      setUsers(data);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Fehler beim Laden der Benutzer: ' + (err.message || 'Unbekannter Fehler'));
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = users;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.company.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(user => 
        statusFilter === 'active' ? user.isActive : !user.isActive
      );
    }

    setFilteredUsers(filtered);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleRoleFilterChange = (event) => {
    setRoleFilter(event.target.value);
    setPage(0);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleMenuClick = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleDialogOpen = (type, user = null) => {
    setDialogType(type);
    if (user) {
      setFormData({
        name: user.name,
        email: user.email,
        role: user.role,
        company: user.company,
        phone: user.phone,
        isActive: user.isActive,
      });
      setSelectedUser(user);
    } else {
      setFormData({
        name: '',
        email: '',
        role: 'user',
        company: '',
        phone: '',
        isActive: true,
      });
      setSelectedUser(null);
    }
    setDialogOpen(true);
    handleMenuClose();
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedUser(null);
    setFormData({
      name: '',
      email: '',
      role: 'user',
      company: '',
      phone: '',
      isActive: true,
    });
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const approveUser = async (userId) => {
    try {
      await ApiService.approveUser(userId);
      
      setUsers(prev => prev.map(user => 
        user.id === userId 
          ? { ...user, isApproved: true, isActive: true }
          : user
      ));
      setSuccess('Benutzer erfolgreich freigeschaltet!');
    } catch (error) {
      console.error('Error approving user:', error);
      setError('Fehler beim Freischalten des Benutzers: ' + (error.message || 'Unbekannter Fehler'));
    }
  };

  const rejectUser = async (userId) => {
    try {
      await ApiService.rejectUser(userId);
      
      setUsers(prev => prev.map(user => 
        user.id === userId 
          ? { ...user, isApproved: false, isActive: false }
          : user
      ));
      setSuccess('Benutzer-Antrag abgelehnt!');
    } catch (error) {
      console.error('Error rejecting user:', error);
      setError('Fehler beim Ablehnen des Benutzers: ' + (error.message || 'Unbekannter Fehler'));
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      if (dialogType === 'create') {
        // Create new user
        const newUser = {
          id: Date.now(),
          ...formData,
          lastLogin: null,
          createdAt: new Date().toISOString(),
          avatar: null,
        };
        setUsers(prev => [...prev, newUser]);
      } else if (dialogType === 'edit') {
        // Update existing user
        setUsers(prev => prev.map(user =>
          user.id === selectedUser.id
            ? { ...user, ...formData }
            : user
        ));
      }
      handleDialogClose();
    } catch (err) {
      setError('Fehler beim Speichern des Benutzers');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (user) => {
    if (window.confirm(`Möchten Sie den Benutzer "${user.name}" wirklich löschen?`)) {
      try {
        await ApiService.deleteUser(user.id);
        setUsers(prev => prev.filter(u => u.id !== user.id));
        setSuccess('Benutzer erfolgreich gelöscht!');
        handleMenuClose();
      } catch (error) {
        console.error('Error deleting user:', error);
        setError('Fehler beim Löschen des Benutzers: ' + (error.message || 'Unbekannter Fehler'));
      }
    }
  };

  const handleToggleStatus = async (user) => {
    try {
      await ApiService.toggleUserStatus(user.id);
      setUsers(prev => prev.map(u =>
        u.id === user.id
          ? { ...u, isActive: !u.isActive }
          : u
      ));
      setSuccess(`Benutzer ${!user.isActive ? 'aktiviert' : 'deaktiviert'}!`);
      handleMenuClose();
    } catch (error) {
      console.error('Error toggling user status:', error);
      setError('Fehler beim Ändern des Benutzerstatus: ' + (error.message || 'Unbekannter Fehler'));
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'manager':
        return 'warning';
      case 'user':
        return 'primary';
      default:
        return 'default';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin':
        return 'Administrator';
      case 'manager':
        return 'Manager';
      case 'user':
        return 'Benutzer';
      default:
        return role;
    }
  };

  const getStatusChip = (isActive) => (
    <Chip
      label={isActive ? 'Aktiv' : 'Inaktiv'}
      color={isActive ? 'success' : 'default'}
      size="small"
      icon={isActive ? <CheckCircleIcon /> : <BlockIcon />}
    />
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                Benutzerverwaltung
              </Typography>
              <Tooltip
                title={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#FFD700' }}>
                      Benutzerverwaltung
                    </Typography>
                    <Typography variant="body2" sx={{ maxWidth: 300, lineHeight: 1.4 }}>
                      Hier können Sie alle Benutzerkonten verwalten, neue Benutzer erstellen, 
                      Berechtigungen zuweisen und Benutzer aktivieren oder deaktivieren.
                    </Typography>
                  </Box>
                }
                placement="right"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      maxWidth: 350,
                      backgroundColor: 'rgba(0, 0, 0, 0.9)',
                      '& .MuiTooltip-arrow': {
                        color: 'rgba(0, 0, 0, 0.9)',
                      },
                    },
                  },
                }}
              >
                <IconButton
                  size="small"
                  sx={{
                    p: 0.5,
                    color: '#FFD700',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 215, 0, 0.1)',
                      color: '#F57F17',
                    },
                  }}
                >
                  <InfoIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="body1" color="text.secondary">
              Verwalten Sie Benutzer und deren Berechtigungen
            </Typography>
          </Box>
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleDialogOpen('create')}
            sx={{
              background: 'linear-gradient(135deg, #FFD700 0%, #F57F17 100%)',
              color: '#000000',
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #F57F17 0%, #FFD700 100%)',
                boxShadow: '0 6px 16px rgba(255, 215, 0, 0.4)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Neuer Benutzer
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Benutzer suchen..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ borderRadius: 2 }}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Rolle</InputLabel>
                <Select
                  value={roleFilter}
                  onChange={handleRoleFilterChange}
                  label="Rolle"
                >
                  <MenuItem value="all">Alle Rollen</MenuItem>
                  <MenuItem value="admin">Administrator</MenuItem>
                  <MenuItem value="manager">Manager</MenuItem>
                  <MenuItem value="user">Benutzer</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  label="Status"
                >
                  <MenuItem value="all">Alle Status</MenuItem>
                  <MenuItem value="active">Aktiv</MenuItem>
                  <MenuItem value="inactive">Inaktiv</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadUsers}
                disabled={loading}
                fullWidth
                sx={{
                  borderColor: '#FFD700',
                  color: '#FFD700',
                  fontWeight: 600,
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: '#F57F17',
                    backgroundColor: 'rgba(255, 215, 0, 0.1)',
                  },
                }}
              >
                Aktualisieren
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card sx={{ borderRadius: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 600 }}>Benutzer</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Rolle</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Unternehmen</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Letzter Login</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((user) => (
                  <Fade key={user.id} in timeout={300}>
                    <TableRow
                      hover
                      sx={{
                        '&:hover': {
                          backgroundColor: '#f8f9fa',
                        },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar
                            sx={{
                              mr: 2,
                              backgroundColor: '#FFD700',
                              color: '#000000',
                              fontWeight: 600,
                            }}
                          >
                            {user.name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {user.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {user.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        <Chip
                          label={getRoleLabel(user.role)}
                          color={getRoleColor(user.role)}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2">
                          {user.company}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {getStatusChip(user.isActive)}
                          {!user.isApproved && (
                            <Chip
                              label="Wartet auf Freischaltung"
                              color="warning"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {user.lastLogin
                            ? format(new Date(user.lastLogin), 'dd.MM.yyyy HH:mm', { locale: de })
                            : 'Nie'
                          }
                        </Typography>
                      </TableCell>
                      
                      <TableCell align="right">
                        <IconButton
                          onClick={(e) => handleMenuClick(e, user)}
                          size="small"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  </Fade>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
            minWidth: 200,
          },
        }}
      >
        <MenuItem onClick={() => handleDialogOpen('view', selectedUser)}>
          <EditIcon sx={{ mr: 2 }} />
          Anzeigen
        </MenuItem>
        <MenuItem onClick={() => handleDialogOpen('edit', selectedUser)}>
          <EditIcon sx={{ mr: 2 }} />
          Bearbeiten
        </MenuItem>
        {!selectedUser?.isApproved && (
          <>
            <MenuItem onClick={() => approveUser(selectedUser.id)} sx={{ color: 'success.main' }}>
              <CheckCircleIcon sx={{ mr: 2 }} />
              Account freischalten
            </MenuItem>
            <MenuItem onClick={() => rejectUser(selectedUser.id)} sx={{ color: 'warning.main' }}>
              <BlockIcon sx={{ mr: 2 }} />
              Account ablehnen
            </MenuItem>
          </>
        )}
        <MenuItem onClick={() => handleToggleStatus(selectedUser)}>
          {selectedUser?.isActive ? (
            <BlockIcon sx={{ mr: 2 }} />
          ) : (
            <CheckCircleIcon sx={{ mr: 2 }} />
          )}
          {selectedUser?.isActive ? 'Deaktivieren' : 'Aktivieren'}
        </MenuItem>
        <MenuItem onClick={() => handleDelete(selectedUser)} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 2 }} />
          Löschen
        </MenuItem>
      </Menu>

      {/* User Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {dialogType === 'create' ? 'Neuer Benutzer' : 
             dialogType === 'edit' ? 'Benutzer bearbeiten' : 'Benutzer anzeigen'}
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                disabled={dialogType === 'view'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonAddIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="E-Mail"
                type="email"
                value={formData.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
                disabled={dialogType === 'view'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Rolle</InputLabel>
                <Select
                  value={formData.role}
                  onChange={(e) => handleFormChange('role', e.target.value)}
                  label="Rolle"
                  disabled={dialogType === 'view'}
                >
                  <MenuItem value="admin">Administrator</MenuItem>
                  <MenuItem value="manager">Manager</MenuItem>
                  <MenuItem value="user">Benutzer</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Unternehmen"
                value={formData.company}
                onChange={(e) => handleFormChange('company', e.target.value)}
                disabled={dialogType === 'view'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Telefon"
                value={formData.phone}
                onChange={(e) => handleFormChange('phone', e.target.value)}
                disabled={dialogType === 'view'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => handleFormChange('isActive', e.target.checked)}
                    disabled={dialogType === 'view'}
                    color="primary"
                  />
                }
                label="Aktiv"
                sx={{ mt: 2 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleDialogClose}
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
            }}
          >
            Abbrechen
          </Button>
          {dialogType !== 'view' && (
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={loading}
              sx={{
                background: 'linear-gradient(135deg, #FFD700 0%, #F57F17 100%)',
                color: '#000000',
                fontWeight: 600,
                px: 3,
                borderRadius: 2,
                textTransform: 'none',
                '&:hover': {
                  background: 'linear-gradient(135deg, #F57F17 0%, #FFD700 100%)',
                },
              }}
            >
              {loading ? 'Speichere...' : 'Speichern'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
